/**
 * Admin human review queue.
 *
 * Filterable table (status, module, priority) with an "All / My queue" tab.
 * Row → detail dialog showing the student answer, original AI feedback, dislike
 * reasons, and attached question, with Claim → Deliver actions.
 *
 * @module AdminHumanReviewsPage
 */
import { useState } from 'react';
import { ArrowDown, ArrowUp, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { ReviewStatusBadge } from '@/components/humanReview/ReviewStatusBadge';
import { ReviewResultPanel } from '@/components/humanReview/ReviewResultPanel';
import {
  useAdminHumanReviews,
  useAdminReviewDetail,
  useClaimReview,
  useCompleteReview,
} from '@/hooks/useAdminHumanReviews';
import type { AdminReviewDetail } from '@/lib/adminHumanReviewApi';

const STATUSES = ['pending', 'in_progress', 'completed', 'cancelled'] as const;
const MODULES = ['writing', 'speaking'] as const;
const PRIORITIES = ['priority', 'standard'] as const;
const ALL = '__all__';

function DeliverForm({ detail, onDone }: { readonly detail: AdminReviewDetail; readonly onDone: () => void }) {
  const { t } = useTranslation();
  const completeReview = useCompleteReview();
  const [verifiedBand, setVerifiedBand] = useState('');
  const [readinessVerdict, setReadinessVerdict] = useState('');
  const [fixesText, setFixesText] = useState('');
  const [answer, setAnswer] = useState('');

  const handleDeliver = async () => {
    const fixes = fixesText
      .split('\n')
      .map((f) => f.trim())
      .filter(Boolean)
      .slice(0, 3);
    if (!verifiedBand.trim() || !readinessVerdict.trim() || !answer.trim()) {
      toast.error(t('humanReviewAdmin.form.required'));
      return;
    }
    if (fixes.length === 0) {
      toast.error(t('humanReviewAdmin.form.fixesRequired'));
      return;
    }
    try {
      await completeReview.mutateAsync({
        id: detail.id,
        payload: {
          verifiedBand: verifiedBand.trim(),
          readinessVerdict: readinessVerdict.trim(),
          prioritizedFixes: fixes,
          attachedQuestionAnswer: answer.trim(),
        },
      });
      toast.success(t('humanReviewAdmin.deliveredTitle'), {
        description: t('humanReviewAdmin.deliveredMessage'),
      });
      onDone();
    } catch {
      // api layer + throwOnError already surfaced a toast
    }
  };

  return (
    <div className="space-y-4 rounded-lg border border-primary/20 bg-primary/5 p-4">
      <div className="space-y-1.5">
        <Label className="font-ui text-xs font-semibold">{t('humanReviewAdmin.form.verifiedBand')}</Label>
        <Textarea
          value={verifiedBand}
          onChange={(e) => setVerifiedBand(e.target.value)}
          placeholder={t('humanReviewAdmin.form.verifiedBandPlaceholder')}
          rows={1}
        />
        <p className="font-ui text-[11px] text-muted-foreground">{t('humanReviewAdmin.form.verifiedBandHelper')}</p>
      </div>
      <div className="space-y-1.5">
        <Label className="font-ui text-xs font-semibold">{t('humanReviewAdmin.form.readinessVerdict')}</Label>
        <Textarea
          value={readinessVerdict}
          onChange={(e) => setReadinessVerdict(e.target.value)}
          placeholder={t('humanReviewAdmin.form.readinessVerdictPlaceholder')}
          rows={4}
        />
        <p className="font-ui text-[11px] text-muted-foreground">{t('humanReviewAdmin.form.readinessVerdictHelper')}</p>
      </div>
      <div className="space-y-1.5">
        <Label className="font-ui text-xs font-semibold">{t('humanReviewAdmin.form.prioritizedFixes')}</Label>
        <Textarea
          value={fixesText}
          onChange={(e) => setFixesText(e.target.value)}
          placeholder={t('humanReviewAdmin.form.prioritizedFixesPlaceholder')}
          rows={4}
        />
        <p className="font-ui text-[11px] text-muted-foreground">{t('humanReviewAdmin.form.prioritizedFixesHelper')}</p>
      </div>
      <div className="space-y-1.5">
        <Label className="font-ui text-xs font-semibold">{t('humanReviewAdmin.form.attachedQuestionAnswer')}</Label>
        <Textarea
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder={t('humanReviewAdmin.form.attachedQuestionAnswerPlaceholder')}
          rows={3}
        />
        <p className="font-ui text-[11px] text-muted-foreground">{t('humanReviewAdmin.form.attachedQuestionAnswerHelper')}</p>
      </div>
      <Button type="button" size="sm" onClick={handleDeliver} disabled={completeReview.isPending}>
        {completeReview.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden />}
        {completeReview.isPending ? t('humanReviewAdmin.delivering') : t('humanReviewAdmin.deliver')}
      </Button>
    </div>
  );
}

function DetailDialog({ id, onClose }: { readonly id: string; readonly onClose: () => void }) {
  const { t } = useTranslation();
  const { detail, isLoading } = useAdminReviewDetail(id);
  const claimReview = useClaimReview();

  const c = (key: string) => t(`humanReviewAdmin.context.${key}`);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-ui">{t('humanReviewAdmin.title')}</DialogTitle>
        </DialogHeader>

        {isLoading || !detail ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary/40" aria-hidden />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <ReviewStatusBadge status={detail.status} />
              <span className="font-ui text-sm font-semibold">
                {t(`moduleSelection.modules.${detail.module}.label`, { defaultValue: detail.module })}
              </span>
            </div>

            <Field label={c('attachedQuestion')}>{detail.attachedQuestion}</Field>
            {detail.notes && <Field label={c('notes')}>{detail.notes}</Field>}
            {detail.dislikeReasonCodes && detail.dislikeReasonCodes.length > 0 && (
              <Field label={c('dislikeReasons')}>
                {detail.dislikeReasonCodes.join(', ')}
                {detail.dislikeFreeText ? ` — ${detail.dislikeFreeText}` : ''}
              </Field>
            )}
            <Field label={c('answer')}>{detail.targetAnswerText || c('none')}</Field>
            {detail.targetAnswerAudioUrl && (
              <Field label={c('audio')}>
                <audio controls src={detail.targetAnswerAudioUrl} className="w-full" />
              </Field>
            )}
            {detail.aiFeedback && <Field label={c('aiFeedback')}>{detail.aiFeedback}</Field>}

            {detail.status === 'pending' && (
              <Button
                type="button"
                size="sm"
                onClick={() => claimReview.mutate(detail.id)}
                disabled={claimReview.isPending}
              >
                {claimReview.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden />}
                {t('humanReviewAdmin.claim')}
              </Button>
            )}

            {detail.status === 'in_progress' && <DeliverForm detail={detail} onDone={onClose} />}

            {detail.status === 'completed' && detail.result && (
              <ReviewResultPanel result={detail.result} />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { readonly label: string; readonly children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="font-ui text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="font-ui rounded-md border border-border bg-muted/30 p-3 text-sm text-foreground/90 whitespace-pre-wrap">
        {children}
      </div>
    </div>
  );
}

export default function AdminHumanReviewsPage() {
  const { t, i18n } = useTranslation();
  const [mine, setMine] = useState(false);
  const [status, setStatus] = useState<string>(ALL);
  const [module, setModule] = useState<string>(ALL);
  const [priority, setPriority] = useState<string>(ALL);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const { data, isLoading } = useAdminHumanReviews({
    mine,
    status: status === ALL ? undefined : status,
    module: module === ALL ? undefined : module,
    priority: priority === ALL ? undefined : priority,
  });

  const items = [...(data?.items ?? [])].sort((a, b) => {
    const diff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    return sortDir === 'desc' ? -diff : diff;
  });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-ui text-xl font-bold text-foreground">{t('humanReviewAdmin.title')}</h2>
        <p className="font-ui text-sm text-muted-foreground">{t('humanReviewAdmin.subtitle')}</p>
      </div>

      <Tabs value={mine ? 'mine' : 'all'} onValueChange={(v) => setMine(v === 'mine')}>
        <TabsList>
          <TabsTrigger value="all">{t('humanReviewAdmin.tabs.all')}</TabsTrigger>
          <TabsTrigger value="mine">{t('humanReviewAdmin.tabs.mine')}</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex flex-wrap gap-3">
        <FilterSelect
          label={t('humanReviewAdmin.filters.status')}
          value={status}
          onChange={setStatus}
          options={STATUSES.map((s) => ({ value: s, label: t(`humanReview.status.${s}`) }))}
          allLabel={t('humanReviewAdmin.filters.all')}
        />
        <FilterSelect
          label={t('humanReviewAdmin.filters.module')}
          value={module}
          onChange={setModule}
          options={MODULES.map((m) => ({
            value: m,
            label: t(`moduleSelection.modules.${m}.label`, { defaultValue: m }),
          }))}
          allLabel={t('humanReviewAdmin.filters.all')}
        />
        <FilterSelect
          label={t('humanReviewAdmin.filters.priority')}
          value={priority}
          onChange={setPriority}
          options={PRIORITIES.map((p) => ({ value: p, label: p }))}
          allLabel={t('humanReviewAdmin.filters.all')}
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary/40" aria-hidden />
            </div>
          ) : items.length === 0 ? (
            <p className="font-ui p-8 text-center text-sm text-muted-foreground">
              {t('humanReviewAdmin.empty')}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('humanReviewAdmin.columns.user')}</TableHead>
                  <TableHead>{t('humanReviewAdmin.columns.module')}</TableHead>
                  <TableHead>{t('humanReviewAdmin.columns.priority')}</TableHead>
                  <TableHead>{t('humanReviewAdmin.columns.status')}</TableHead>
                  <TableHead>
                    <button
                      type="button"
                      onClick={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
                      className="inline-flex items-center gap-1 font-semibold hover:text-foreground"
                    >
                      {t('humanReviewAdmin.columns.created')}
                      {sortDir === 'desc'
                        ? <ArrowDown className="h-3.5 w-3.5" aria-hidden />
                        : <ArrowUp className="h-3.5 w-3.5" aria-hidden />}
                    </button>
                  </TableHead>
                  <TableHead>{t('humanReviewAdmin.columns.question')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.id}
                    className="cursor-pointer"
                    onClick={() => setSelectedId(item.id)}
                  >
                    <TableCell className="font-ui text-sm">{item.userName ?? '—'}</TableCell>
                    <TableCell className="font-ui text-sm">
                      {t(`moduleSelection.modules.${item.module}.label`, { defaultValue: item.module })}
                    </TableCell>
                    <TableCell className="font-ui text-sm">{item.priority}</TableCell>
                    <TableCell>
                      <ReviewStatusBadge status={item.status} />
                    </TableCell>
                    <TableCell className="font-ui text-sm text-muted-foreground">
                      {new Date(item.createdAt).toLocaleDateString(i18n.language)}
                    </TableCell>
                    <TableCell className="font-ui max-w-xs truncate text-sm">{item.attachedQuestion}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {selectedId && <DetailDialog id={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  allLabel,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly options: { value: string; label: string }[];
  readonly allLabel: string;
}) {
  return (
    <div className="space-y-1">
      <Label className="font-ui text-xs text-muted-foreground">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9 w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{allLabel}</SelectItem>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
